import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useState } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import Popup from 'components/Popup/Popup';
import type { SelectOptionT } from 'declarations/types/select';

import useTicketsReadStatus from './hooks/useTicketsActionDropDown';
import { FlexContainerStyled, MarkReadIconButtonWrapper } from './TicketActionDropDown.styled';

interface SelectProps {
  selectedTickets: string[];
  setSelectedTickets: (value: string[]) => void;
}

const TicketsReadStatusSelect: React.FC<SelectProps> = ({
  selectedTickets,
  setSelectedTickets,
}): JSX.Element => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const {
    errorIsVisible,
    isPopupOpen,
    readStatusTicketErrorMessage,
    unreadStatusTicketErrorMessage,
    handleOptionChange,
    handlePopupOpen,
    handlePopupClose,
  } = useTicketsReadStatus({ selectedTickets, setSelectedTickets });

  const handleIconClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (selectedTickets.length > 0) {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleMenuClose = (): void => {
    setMenuAnchor(null);
  };

  const handleMarkRead = (): void => {
    handleOptionChange({ value: 'read', label: 'Mark Read' } as SelectOptionT);
    handleMenuClose();
  };

  const handleMarkUnread = (): void => {
    handleOptionChange({ value: 'unread', label: 'Mark Unread' } as SelectOptionT);
    handleMenuClose();
  };

  return (
    <MarkReadIconButtonWrapper>
      {(errorIsVisible || readStatusTicketErrorMessage || unreadStatusTicketErrorMessage) && (
        <Popup
          showCloseBtn
          isOpen={isPopupOpen}
          onOpen={handlePopupOpen}
          onClose={handlePopupClose}
        >
          <FlexContainerStyled direction="column">
            <ErrorSection />
          </FlexContainerStyled>
        </Popup>
      )}
      <Tooltip title={selectedTickets.length > 0 ? 'Mark as Read' : 'Select tickets to mark'}>
        <span>
          <IconButton
            id="mark-read-button"
            size="small"
            onClick={handleIconClick}
            disabled={selectedTickets.length === 0}
            aria-label="Mark as Read"
            aria-controls={menuOpen ? 'mark-read-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
            sx={{
              padding: 0,
              backgroundColor: 'inherit',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <ArrowDropDownIcon fontSize="small" sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id="mark-read-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'mark-read-button' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={handleMarkRead}>Mark Read</MenuItem>
        <MenuItem onClick={handleMarkUnread}>Mark Unread</MenuItem>
      </Menu>
    </MarkReadIconButtonWrapper>
  );
};

export default TicketsReadStatusSelect;
